import { getFeaturesForOne, IpaType } from "../../phonemes/ipa";
import { compileRuleSet } from "../../rules/compileRuleSet";
import { SegmentStrategy } from "../../rules/intoSegments";
import { PhoneLengths } from "../../rules/lengthRules";
import { Rule } from "../../rules/rule";
import { RuleSet, CompiledRuleSet, getJustRules } from "../../rules/ruleSet";
import { runRulesOnIpa } from "../../rules/runRules";
import { StressRule } from "../../stress/stressRule";
import { ruleSetToString } from "../customRules/ruleSetToString";

const defaultRule: RuleSet = { inputSegments: SegmentStrategy.Single, outputSegments: SegmentStrategy.Ipa, preprocess: ['normIpa'] };

/** tracking user rules for fixing up input */
export class FixupRules {
  private ruleSet = defaultRule;
  private rulesAsString = '';
  private compiledRuleSet: CompiledRuleSet;

  constructor() {
    const [compiled] = compileRuleSet(this.ruleSet);
    this.compiledRuleSet = compiled;
  }

  clear() {
    this.ruleSet = defaultRule;
    const [compiled] = compileRuleSet(this.ruleSet);
    this.compiledRuleSet = compiled;
  }

  getRules(): RuleSet { return this.ruleSet; }
  getRulesAsString(): string { return this.rulesAsString; }

  /** add from>to */
  addChange(from: string, to: string) {
    if (to === undefined) {
      to = '';
    }
    const justRules = getJustRules(this.ruleSet.rules);
    if (justRules && justRules.some(r => r.from === from && r.to === to)) {
      return; // rule already exists
    }

    // replace existing rule
    if (justRules && justRules.some(r => r.from === from)) {
      const rules = justRules.filter(r => r.from !== from);
      rules.push({ from, to });
      this.newRuleSet({ ...this.ruleSet, rules });
    } else {
      // new rule
      const rules: Rule[] = justRules ?? [];
      rules.push({ from, to });
      this.newRuleSet({ ...this.ruleSet, rules });
    }
  }

  /** is there currently a from>to rule? */
  hasChange(from: string, to: string): boolean {
    const justRules = getJustRules(this.ruleSet.rules);
    return this.ruleSet && justRules !== undefined && justRules.some(r => r.from === from && r.to === to);
  }

  /** if there's currently a rule converting 'from', return what it's converted to */
  getTo(from: string): IpaType | null {
    const justRules = getJustRules(this.ruleSet.rules);
    if (!this.ruleSet || justRules === undefined) {
      return null;
    }
    const to = justRules.filter(r => r.from === from);
    return to.length === 0 ? null : to[0].to;
  }

  /** add primary stress to the specified syllable, count from end if negative, remove if 0 */
  addStress(syllable: number) {
    if (syllable === 0) {
      const { stressRule, ...ruleSet } = this.ruleSet;
      this.newRuleSet(ruleSet);
    } else {
      const stressRule: StressRule<IpaType> = { default: syllable };
      this.newRuleSet({ ...this.ruleSet, stressRule });
    }
  }

  /** set the length for a phoneme */
  setPhonemeLength(phoneme: IpaType, length: number) {
    let lengthMap = this.ruleSet.phonemeLengths?.map ?? {};
    length = Math.max(Math.min(length, 580), 40); // clamp to [40,580]
    lengthMap[phoneme] = length;
    this.newRuleSet({ ...this.ruleSet, phonemeLengths: { map: lengthMap } });
  }
  clearPhonemeLengths() {
    this.newRuleSet({ ...this.ruleSet, phonemeLengths: { map: {} } });
  }

  /** get the complete collection of phoneme lengths */
  getLengthMap() { return this.ruleSet.phonemeLengths?.map ?? {}; }
  /** get rules for adjusting phoneme lengths before speaking */
  getPhonemeLengthRules(): PhoneLengths | undefined {
    return this.compiledRuleSet.addPhonemeLength;
  }

  runRules(input: string): IpaType {
    input = this.stripDelimiters(input);
    return runRulesOnIpa(input, this.compiledRuleSet).ipa;
  }

  private newRuleSet(ruleSet: RuleSet) {
    this.ruleSet = ruleSet;
    this.rulesAsString = ruleSetToString(this.ruleSet);

    const [compiled] = compileRuleSet(this.ruleSet);
    this.compiledRuleSet = compiled;
  }

  private stripDelimiters(input: string): string {
    let s = '';
    for (const c of input) {
      const features = getFeaturesForOne(c);
      if (features === undefined || !features.includes('delimit')) {
        s += c;
      }
    }
    return s;
  }
}
